import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, CheckCircle2, Sparkles, ArrowRight, HelpCircle, ChevronDown } from 'lucide-react';

function AccordionItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        style={{ width: '100%', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'Poppins' }}>{question}</span>
        <ChevronDown size={18} style={{ color: 'var(--text-secondary)', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--bg-color)' }}
          >
            <div style={{ padding: '20px 24px', color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', fontWeight: 600 }}>
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Admissions() {
  const navigate = useNavigate();

  const steps = [
    { num: "01", title: "Submit Online Inquiry", desc: "Fill in the digital student application form online with core credentials.", color: 'var(--primary-color)' },
    { num: "02", title: "Document Check", desc: "Bring student's birth certificate, Aadhaar, and previous school report cards.", color: 'var(--accent-purple)' },
    { num: "03", title: "Interaction / Counseling", desc: "A brief welcoming interaction between child, parents, and school mentors.", color: 'var(--accent-coral)' },
    { num: "04", title: "Obtain Receipt", desc: "Pay the prescribed admission fees to secure the student's classroom seat.", color: 'var(--accent-green)' }
  ];

  const faqs = [
    { question: "What is the age criteria for Pre-Nursery?", answer: "The child should be 2.5+ years of age as of June 1st of the academic session." },
    { question: "What is the fee payment cycle?", answer: "Fees can be paid in quarterly installments or as a full session payment at the start of the academic year." },
    { question: "Is school transportation available?", answer: "Yes, school bus services are available across major routes in Bilaspur. You can request this in the admission form." },
    { question: "Are parents required to attend interviews?", answer: "We conduct a soft counseling session to understand the child's milestones and discuss curriculum integration with parents." }
  ];

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-admissions)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative' }}>
      
      {/* Floating background details */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>

      <div className="page-container-wide">
        
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ textAlign: 'center', marginBottom: '50px' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-coral)', textTransform: 'uppercase', letterSpacing: '1px' }}>Join Our Family</span>
          <h1 style={{ fontSize: '2.75rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>School Admissions</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: '1.05rem' }}>Secure a bright educational future for your child at Vamshee Techno School.</p>
        </motion.div>

        {/* Timeline stepper process */}
        <div style={{ marginBottom: '60px' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 style={{ fontSize: '1.75rem', color: 'var(--text-primary)', marginBottom: '24px', fontFamily: 'Poppins' }}>Admission Process Timeline</h2>
          </motion.div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {steps.map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.12 }}
                style={{ backgroundColor: '#ffffff', padding: '32px 24px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', borderTop: `6px solid ${step.color}` }}
              >
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: step.color, backgroundColor: 'var(--bg-color)', width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `2px solid ${step.color}` }}>
                    {step.num}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.15rem', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: 800, marginTop: '4px', fontFamily: 'Poppins', lineHeight: '1.3' }}>{step.title}</h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.5', margin: 0, fontWeight: 600 }}>{step.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Age Criteria & Fee structures */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px', marginBottom: '60px' }}>
          
          {/* Class eligibility */}
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ backgroundColor: '#ffffff', padding: '32px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', borderTop: '6px solid var(--primary-color)' }}>
            <h3 style={{ fontSize: '1.35rem', color: 'var(--text-primary)', marginBottom: '20px', fontFamily: 'Poppins', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Info size={20} style={{ color: 'var(--primary-color)' }} /> Grade Eligibility Metrics
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid var(--border-color)', fontSize: '0.98rem', fontWeight: 600 }}>
                <span>Pre-Nursery</span>
                <span style={{ color: 'var(--text-secondary)' }}>2.5+ Years as of June 1st</span>
              </li>
              <li style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid var(--border-color)', fontSize: '0.98rem', fontWeight: 600 }}>
                <span>Nursery</span>
                <span style={{ color: 'var(--text-secondary)' }}>3+ Years as of June 1st</span>
              </li>
              <li style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid var(--border-color)', fontSize: '0.98rem', fontWeight: 600 }}>
                <span>KG-I & KG-II</span>
                <span style={{ color: 'var(--text-secondary)' }}>4+ / 5+ Years</span>
              </li>
              <li style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.98rem', fontWeight: 600 }}>
                <span>Primary School (I-V)</span>
                <span style={{ color: 'var(--text-secondary)' }}>6+ Years</span>
              </li>
            </ul>
          </motion.div>

          {/* Fee guidelines structure */}
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ backgroundColor: '#ffffff', padding: '32px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', borderTop: '6px solid var(--accent-purple)' }}>
            <h3 style={{ fontSize: '1.35rem', color: 'var(--text-primary)', marginBottom: '20px', fontFamily: 'Poppins', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} style={{ color: 'var(--accent-purple)' }} /> Fee Payment Structure
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '16px', fontWeight: 600 }}>
              Fees are structured quarterly to reduce payment pressure on parents. Direct online bank transfers and office cash options are supported.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '6px 12px', backgroundColor: 'var(--bg-color)', borderRadius: '6px' }}>
                <span style={{ fontWeight: 700 }}>Quarter 1 (June)</span>
                <span style={{ color: 'var(--primary-dark)', fontWeight: 700 }}>Admission + Term 1</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '6px 12px', backgroundColor: 'var(--bg-color)', borderRadius: '6px' }}>
                <span style={{ fontWeight: 700 }}>Quarter 2 (Sept)</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>Term 2 Fees</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', padding: '6px 12px', backgroundColor: 'var(--bg-color)', borderRadius: '6px' }}>
                <span style={{ fontWeight: 700 }}>Quarter 3 / 4</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>Term 3 & Exam Fees</span>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Required Documents checklist */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ backgroundColor: '#ffffff', padding: '40px', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', marginBottom: '60px' }}>
          <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '24px', fontFamily: 'Poppins', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CheckCircle2 size={24} style={{ color: 'var(--accent-green)' }} /> Required Enrollment Credentials
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {["Child's Birth Certificate photocopy",
              "Aadhaar card copy of student & parents",
              "Transfer Certificate (original from previous school)",
              "Class milestone scorecard / Report card copy",
              "Passport size photographs (4 copies)",
              "Address and identification verification cards"].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '0.98rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  <span style={{ color: 'var(--accent-green)', fontSize: '1.25rem', fontWeight: 800 }}>✓</span>
                  <span>{item}</span>
                </div>
              ))}
          </div>
        </motion.div>

        {/* FAQ Accordion Section */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginBottom: '60px' }}>
          <h2 style={{ fontSize: '1.75rem', color: 'var(--text-primary)', marginBottom: '24px', fontFamily: 'Poppins', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HelpCircle size={22} style={{ color: 'var(--accent-coral)' }} /> Frequently Asked Questions (FAQ)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </motion.div>

        {/* Bottom Call to Action online form redirect */}
        <motion.div initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ backgroundColor: 'var(--primary-color)', color: '#ffffff', padding: '40px', borderRadius: 'var(--border-radius-card)', textAlign: 'center', boxShadow: 'var(--shadow-premium)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <h3 style={{ color: '#ffffff', fontSize: '1.85rem', marginBottom: '4px', fontFamily: 'Poppins', fontWeight: 800 }}>Begin the Admissions Registration</h3>
          <p style={{ color: '#f0fdf4', maxWidth: '600px', margin: '0 auto', fontSize: '1.05rem', fontWeight: 700 }}>
            Submit our online admission registration form to trigger background seat reservations.
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
            <button onClick={() => navigate('/Apply')} className="btn-premium btn-premium-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              Online Application Form <ArrowRight size={18} />
            </button>
            <a href="tel:8982397404" className="btn-premium btn-premium-outline" style={{ border: '2px solid #ffffff', color: '#ffffff', textDecoration: 'none' }}>
              Call Helpline
            </a>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
